/* global axios */
import ApiClient from '../ApiClient';

class AiAgentsAPI extends ApiClient {
  constructor() {
    super('ai_agents', { accountScoped: true, saas: true });
  }

  getKnowledgeBases(agentId) {
    return axios.get(`${this.url}/${agentId}/knowledge_bases`);
  }

  getKnowledgeBase(agentId, kbId) {
    return axios.get(`${this.url}/${agentId}/knowledge_bases/${kbId}`);
  }

  createKnowledgeBase(agentId, data) {
    return axios.post(`${this.url}/${agentId}/knowledge_bases`, {
      knowledge_base: data,
    });
  }

  updateKnowledgeBase(agentId, kbId, data) {
    return axios.patch(`${this.url}/${agentId}/knowledge_bases/${kbId}`, {
      knowledge_base: data,
    });
  }

  deleteKnowledgeBase(agentId, kbId) {
    return axios.delete(`${this.url}/${agentId}/knowledge_bases/${kbId}`);
  }

  createDocument(agentId, kbId, data, isFormData = false) {
    if (isFormData) {
      return axios.post(
        `${this.url}/${agentId}/knowledge_bases/${kbId}/knowledge_documents`,
        data,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    }
    return axios.post(
      `${this.url}/${agentId}/knowledge_bases/${kbId}/knowledge_documents`,
      { knowledge_document: data }
    );
  }

  deleteDocument(agentId, kbId, docId) {
    return axios.delete(
      `${this.url}/${agentId}/knowledge_bases/${kbId}/knowledge_documents/${docId}`
    );
  }

  getDocuments(agentId, kbId) {
    return axios.get(
      `${this.url}/${agentId}/knowledge_bases/${kbId}/knowledge_documents`
    );
  }

  searchKnowledge(agentId, query, limit = 5) {
    return axios.post(`${this.url}/${agentId}/knowledge_bases/search`, {
      query,
      limit,
    });
  }

  getTools(agentId) {
    return axios.get(`${this.url}/${agentId}/agent_tools`);
  }

  createTool(agentId, data) {
    return axios.post(`${this.url}/${agentId}/agent_tools`, {
      agent_tool: data,
    });
  }

  updateTool(agentId, toolId, data) {
    return axios.patch(`${this.url}/${agentId}/agent_tools/${toolId}`, {
      agent_tool: data,
    });
  }

  deleteTool(agentId, toolId) {
    return axios.delete(`${this.url}/${agentId}/agent_tools/${toolId}`);
  }

  importOpenApi(agentId, data) {
    return axios.post(
      `${this.url}/${agentId}/agent_tools/import_openapi`,
      data
    );
  }

  createInboxLink(agentId, data) {
    return axios.post(`${this.url}/${agentId}/ai_agent_inboxes`, {
      ai_agent_inbox: data,
    });
  }

  updateInboxLink(agentId, linkId, data) {
    return axios.patch(`${this.url}/${agentId}/ai_agent_inboxes/${linkId}`, {
      ai_agent_inbox: data,
    });
  }

  deleteInboxLink(agentId, linkId) {
    return axios.delete(`${this.url}/${agentId}/ai_agent_inboxes/${linkId}`);
  }

  saveWorkflow(agentId, workflow) {
    return axios.patch(`${this.url}/${agentId}`, {
      ai_agent: { draft_workflow: workflow },
    });
  }

  publishWorkflow(agentId) {
    return axios.post(`${this.url}/${agentId}/publish_workflow`);
  }

  discardDraft(agentId) {
    return axios.post(`${this.url}/${agentId}/discard_draft`);
  }

  clone(agentId) {
    return axios.post(`${this.url}/${agentId}/clone`);
  }

  getAnalytics(agentId, params = {}) {
    return axios.get(`${this.url}/${agentId}/analytics`, { params });
  }

  getWorkflowRuns(agentId, params = {}) {
    return axios.get(`${this.url}/${agentId}/workflow_runs`, { params });
  }

  getWorkflowRun(agentId, runId) {
    return axios.get(`${this.url}/${agentId}/workflow_runs/${runId}`);
  }

  /**
   * Send a preview/test message to the agent without creating a real conversation.
   * When stream=true, returns { request_id, stream: true } — subscribe to LlmChannel.
   */
  preview(agentId, payload) {
    return axios.post(`${this.url}/${agentId}/preview`, payload);
  }

  /**
   * Fetch available voices and models for the given provider.
   * Returns { voices: [...], models: [...] }
   */
  getVoiceCatalog(agentId, provider) {
    return axios.get(`${this.url}/${agentId}/voice_catalog`, {
      params: { provider },
    });
  }

  /**
   * Synthesize a short text preview for the given voice.
   * Returns { audio: 'base64...', content_type: 'audio/mpeg' }
   */
  previewVoice(agentId, { voiceId, text, modelId, provider, language }) {
    return axios.post(`${this.url}/${agentId}/voice_preview`, {
      voice_id: voiceId,
      text,
      model_id: modelId,
      provider,
      language,
    });
  }

  getConversationAiUsage(conversationId) {
    const baseUrl = this.url.replace('/ai_agents', '/conversations');
    return axios.get(`${baseUrl}/${conversationId}/ai_usage`);
  }

  getMonitor(params = {}) {
    return axios.get(`${this.url}/monitor`, { params });
  }

  getOwnershipOverview() {
    return axios.get(`${this.url}/ownership_overview`);
  }

  getMetaBusinessAgentPanel({ phoneNumberId, inboxId }) {
    const baseUrl = this.url.replace('/ai_agents', '/ai_manager');
    return axios.get(
      `${baseUrl}/meta_business_agent/${encodeURIComponent(phoneNumberId)}/panels`,
      { params: { inbox_id: inboxId } }
    );
  }

  getRuntimePanel({ inboxId, phoneNumberId }) {
    const baseUrl = this.url.replace('/ai_agents', '/ai_manager');
    return axios.get(
      `${baseUrl}/runtimes/${encodeURIComponent(inboxId)}/panels`,
      { params: { phone_number_id: phoneNumberId } }
    );
  }

  getDeployRollbackPanel({ inboxId, phoneNumberId }) {
    const baseUrl = this.url.replace('/ai_agents', '/ai_manager');
    return axios.get(
      `${baseUrl}/deployments/${encodeURIComponent(inboxId)}/panels`,
      { params: { phone_number_id: phoneNumberId } }
    );
  }

  applyModeTransition(data) {
    return axios.post(`${this.url}/mode_transition`, {
      mode_transition: data,
    });
  }

  previewModeTransition(data) {
    const baseUrl = this.url.replace('/ai_agents', '/ai_manager');
    return axios.post(`${baseUrl}/mode_transition_preview`, {
      mode_transition: data,
    });
  }

  getVersions(agentId) {
    return axios.get(`${this.url}/${agentId}/versions`);
  }

  createVersion(agentId, label = '') {
    return axios.post(`${this.url}/${agentId}/create_version`, { label });
  }

  restoreVersion(agentId, versionNumber) {
    return axios.post(
      `${this.url}/${agentId}/versions/${versionNumber}/restore`
    );
  }
}

export default new AiAgentsAPI();
